import { MdsIcon, MdsIconKind } from "@/design-system/components/icon";
import { MdsText, MdsTextProps, MdsTextSize, MdsTextWeight } from "@/design-system/components/text";
import { mdsColors } from "@/design-system/foundations";
import { MDSButton } from "@/mds";
import type { IMDSButtonProps } from "@/mds/components/buttons/button/button.h";
import styled from "@emotion/styled";
import { motion } from "motion/react";

export const WizardContainer = styled(motion.div)`
  display: flex;
  flex-direction: column;
  padding: ${({ theme }) => theme.spacing.lg};
  background: white;
  border-radius: ${({ theme }) => theme.borderRadius.large}px;
  overflow: hidden;
  box-shadow: ${({ theme }) => theme.shadows.hoverCard};
  width: 592px;
  min-height: 80px;
  max-height: min(540px, 95dvh);
  max-width: 90vw;
`;
WizardContainer.displayName = "WizardContainer";

export const StepTitle: React.FC<MdsTextProps> = props => (
  <MdsText
    {...props}
    size={MdsTextSize.Large}
    weight={MdsTextWeight.Bold}
    color={mdsColors().grey.x600}
  />
);

export const TabsContainer = styled.div`
  display: flex;
  flex-direction: row;
  gap: ${({ theme }) => theme.spacing.md};
  margin-top: -4px;
  border-bottom: 2px solid ${({ theme }) => theme.colors.grey.x100};
`;
TabsContainer.displayName = "TabsContainer";

export const Tab = styled.button<{ isActive: boolean }>`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.md};
  padding: ${({ theme }) => theme.spacing.sm} 0px;
  cursor: pointer;
  transition: all 0.2s ease;
  white-space: nowrap;
  color: ${({ theme, isActive }) => (isActive ? theme.colors.grey.x600 : theme.colors.grey.x400)};
  border-bottom: ${({ theme, isActive }) =>
    isActive ? `2px solid ${theme.colors.grey.x600}` : "none"};
  margin-bottom: -2px;

  font-size: ${({ theme }) => theme.fontSizes.smedium};
  font-weight: ${({ theme }) => theme.fontWeights.medium};

  &:hover {
    color: ${({ theme }) => theme.colors.grey.x600};
  }
`;
Tab.displayName = "Tab";

export const FooterContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  font-size: ${({ theme }) => theme.fontSizes.small};
  gap: ${({ theme }) => theme.spacing.md};
  margin-top: ${({ theme }) => theme.spacing.sm};
  background-color: white;
`;
FooterContainer.displayName = "FooterContainer";

export const FooterButtons = styled.div`
  display: flex;
  flex-direction: row;
  width: 100%;
  align-items: center;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing.md};
`;
FooterButtons.displayName = "FooterButtons";

const StyledFooterButton = styled(MDSButton)`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 4px;

  ${props =>
    props.isDisabled &&
    `
    opacity: 0;
    `}

  &[data-disabled] {
    cursor: default;
  }
`;

export const FooterButton = (props: IMDSButtonProps) => <StyledFooterButton {...props} size="sm" />;

export const FooterBackButton = (props: IMDSButtonProps & { isDisabled?: boolean }) => (
  <StyledFooterButton {...props} size="sm">
    <MdsIcon kind={MdsIconKind.LeftArrow} />
    Back
  </StyledFooterButton>
);

export const FooterNextButton = (props: IMDSButtonProps & { isDisabled?: boolean }) => (
  <StyledFooterButton {...props} size="sm">
    Next
    <MdsIcon kind={MdsIconKind.RightArrow} />
  </StyledFooterButton>
);

export const SkipLink = styled.button`
  background: none;
  border: none;
  color: ${({ theme }) => theme.colors.grey.x400};
  font-size: ${({ theme }) => theme.fontSizes.small};
  cursor: pointer;
  text-decoration: underline;
  padding: ${({ theme }) => theme.spacing.xxs} ${({ theme }) => theme.spacing.xs};
  transition: color 0.2s ease;

  &:hover {
    color: ${({ theme }) => theme.colors.grey.x500};
  }

  &:focus {
    outline: none;
    color: ${({ theme }) => theme.colors.grey.x600};
  }
`;
SkipLink.displayName = "SkipLink";

export const WizardContentWrapper = styled(motion.div)`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.sm};
`;
WizardContentWrapper.displayName = "WizardContentWrapper";

export const FeaturedApp = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  align-self: center;
  border-radius: ${({ theme }) => theme.borderRadius.mediumLarge}px;
  background: ${({ theme }) => theme.colors.mblue.x50};
  padding: ${({ theme }) => theme.spacing.lg} ${({ theme }) => theme.spacing.xl};
`;
FeaturedApp.displayName = "FeaturedApp";
